import { useState, useEffect, useCallback, useRef } from "react";
import API_BASE_URL from "../../config";
import { useAuth } from "../../AuthContext";
import useJobs from "../../hooks/useJobs";
import useActiveJobId from "../../hooks/useActiveJobId";
import useJobWaiter from "../../hooks/useJobWaiter";
import DataTable from "./DataTable";
import DocumentPicker from "./DocumentPicker";
import { stripUuid } from "./complianceExport";
import "./CompliancePipeline.css";

const SECTION_LABELS = {
  dimensions: "1 · Maße & Geometrie",
  material: "2 · Werkstoff- & mechanische Eigenschaften",
  qc: "3 · Güteüberwachung (QC)",
  labeling: "4 · Kennzeichnung & Lieferung",
  nonconformance: "5 · Nichtübereinstimmung",
};

// tone: a design-system badge tone (warning | danger | …).
function Badge({ tone, children }) {
  return <span className={`ds-badge ds-badge--${tone} cp-badge`}>{children}</span>;
}

function SourceCell({ source }) {
  return <span className="cp-src">{source?.location || "—"}</span>;
}

/** Render one extracted section as a table per its schema kind. */
function SectionView({ section }) {
  const k = section.kind;
  if (k === "dimensions") {
    return (
      <DataTable>
        <thead><tr><th>Parameter</th><th>Wert / Grenze</th><th>Toleranz</th><th>Quelle</th></tr></thead>
        <tbody>
          {section.rows.map((r, i) => (
            <tr key={i}>
              <td>{r.parameter} {r.requires_drawing && <Badge tone="warning">See drawing</Badge>}</td>
              <td>{r.value}</td><td>{r.tolerance || "—"}</td><td><SourceCell source={r.source} /></td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    );
  }
  if (k === "material") {
    return (
      <>
        <h5 className="cp-subhead">2a · Brick properties</h5>
        <DataTable>
          <thead><tr><th>Property</th><th>Typ</th><th>Mittelwert</th><th>Einzelwert</th><th>Prüfnorm</th><th>Quelle</th></tr></thead>
          <tbody>
            {section.brick_properties.map((r, i) => (
              <tr key={i}><td>{r.property}</td><td>{r.brick_type || "—"}</td><td>{r.mean_value || "—"}</td>
                <td>{r.single_value || "—"}</td><td>{r.test_norm || "—"}</td><td><SourceCell source={r.source} /></td></tr>
            ))}
          </tbody>
        </DataTable>
        <h5 className="cp-subhead">2b · Dämmstofffüllung</h5>
        <DataTable>
          <thead><tr><th>Property</th><th>TV 8+</th><th>TV 9+</th><th>Prüfnorm</th><th>Quelle</th></tr></thead>
          <tbody>
            {section.insulation_properties.map((r, i) => (
              <tr key={i}><td>{r.property}</td><td>{r.tv8_plus || "—"}</td><td>{r.tv9_plus || "—"}</td>
                <td>{r.test_norm || "—"}</td><td><SourceCell source={r.source} /></td></tr>
            ))}
          </tbody>
        </DataTable>
      </>
    );
  }
  if (k === "qc") {
    return (
      <>
        <DataTable>
          <thead><tr><th>Test</th><th>Beschreibung</th><th>WPK</th><th>EP</th><th>FÜ</th><th>Kriterium</th><th>Norm</th><th>Quelle</th></tr></thead>
          <tbody>
            {section.rows.map((r, i) => (
              <tr key={i}><td>{r.test_id}{r.footnote_markers?.length ? <sup>{r.footnote_markers.join(",")}</sup> : null}</td>
                <td>{r.description}</td><td>{r.wpk_frequency || "—"}</td><td>{r.ep || "—"}</td><td>{r.fue_frequency || "—"}</td>
                <td>{r.pass_criterion || "—"} {r.third_party && <Badge tone="danger">Third party</Badge>}</td>
                <td>{r.test_norm || "—"}</td><td><SourceCell source={r.source} /></td></tr>
            ))}
          </tbody>
        </DataTable>
        {section.footnotes?.length > 0 && (
          <ol className="cp-footnotes">
            {section.footnotes.map((f, i) => <li key={i}><b>{f.marker}</b> {f.text}</li>)}
          </ol>
        )}
      </>
    );
  }
  if (k === "labeling") {
    return (
      <DataTable>
        <thead><tr><th>Feld</th><th>Erforderlicher Inhalt</th><th>Quelle</th></tr></thead>
        <tbody>
          {section.items.map((r, i) => (
            <tr key={i}><td>{r.field}</td><td>{r.required_content || "—"}</td><td><SourceCell source={r.source} /></td></tr>
          ))}
        </tbody>
      </DataTable>
    );
  }
  if (k === "nonconformance") {
    return (
      <ol className="cp-steps">
        {section.steps.map((s, i) => <li key={i}>{s.action} <SourceCell source={s.source} /></li>)}
      </ol>
    );
  }
  return null;
}

export default function CompliancePipeline({ database }) {
  const { user } = useAuth();
  const currentUser = () => user?.email || "engineer";
  const [zDocs, setZDocs] = useState([]);
  const [docId, setDocId] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | mapping | mapped | extracting | done
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");
  const [pageMap, setPageMap] = useState(null);
  const [brief, setBrief] = useState(null);
  const [signoffs, setSignoffs] = useState({});
  const { startJob } = useJobs({ enabled: false }); // only need startJob; no polling here
  const { waitFor } = useJobWaiter();
  // Persists the running job so we can re-attach after navigating away and back.
  const [activeJob, setActiveJob, clearActiveJob] = useActiveJobId("approvalPipelineActiveJob");
  // Cancels an in-flight job poll on unmount / when a newer run starts.
  const pollTokenRef = useRef(null);
  useEffect(() => () => { pollTokenRef.current = null; }, []);

  // Wait for a background job to complete via the shared jobs SSE feed, guarded by
  // the shared poll token so a stale wait never writes state after unmount or after
  // a newer run started.
  const pollJob = useCallback((jobId, token, { onDone, onFailed }) => {
    waitFor(jobId)
      .then((job) => {
        if (pollTokenRef.current !== token) return; // cancelled
        if (job.status === "failed") onFailed(job.error || "unbekannt");
        else onDone(job);
      })
      .catch((err) => {
        if (pollTokenRef.current === token) onFailed(err.message);
      });
  }, [waitFor]);

  const loadSignoffs = useCallback(async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/approval/pipeline/signoff/${id}`);
      if (res.ok) setSignoffs((await res.json()).signoffs || {});
    } catch { /* non-fatal */ }
  }, []);

  const approveSection = useCallback(async (sectionKey) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/approval/pipeline/signoff`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: docId, section_key: sectionKey,
                               user: currentUser(), database: database || "Betonbau" }),
      });
      if (res.ok) setSignoffs((await res.json()).signoffs || {});
    } catch { /* non-fatal */ }
  }, [docId, database]);

  useEffect(() => {
    const controller = new AbortController();
    const qs = database ? `?database=${encodeURIComponent(database)}` : "";
    fetch(`${API_BASE_URL}/api/v1/approval/z-documents${qs}`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : { documents: [] }))
      .then((d) => setZDocs(d.documents || []))
      .catch((e) => { if (e.name !== "AbortError") setZDocs([]); });
    return () => controller.abort();
  }, [database]);

  const analyse = useCallback(async () => {
    if (!docId) return;
    setPhase("mapping"); setError(null); setNotice(""); setPageMap(null); setBrief(null);
    const token = {};
    pollTokenRef.current = token;
    try {
      const data = await startJob("/api/v1/approval/pipeline/structure", {
        document_id: docId, database: database || "Betonbau",
      });
      if (!data.job_id) { // synchronous fallback (jobs disabled)
        if (pollTokenRef.current === token) { setPageMap(data.result); setPhase("mapped"); }
        return;
      }
      setActiveJob({ jobId: data.job_id, step: "structure", docId });
      setNotice("Struktur-Analyse läuft im Hintergrund…");
      pollJob(data.job_id, token, {
        onDone: (job) => { clearActiveJob(); setNotice(""); setPageMap(job.result); setPhase("mapped"); },
        onFailed: (msg) => {
          clearActiveJob(); setNotice("");
          setError(`Structure analysis failed: ${msg}`); setPhase("idle");
        },
      });
    } catch (e) {
      setNotice(""); setError(`Structure analysis failed: ${e.message}`); setPhase("idle");
    }
  }, [docId, database, startJob, pollJob, setActiveJob, clearActiveJob]);

  const updatePages = (idx, value) => {
    const pages = value.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
    setPageMap((pm) => {
      const sections = pm.sections.map((s, i) => (i === idx ? { ...s, pages } : s));
      return { ...pm, sections };
    });
  };

  const extract = useCallback(async () => {
    setPhase("extracting"); setError(null); setNotice(""); setBrief(null);
    const token = {};
    pollTokenRef.current = token;
    try {
      const data = await startJob("/api/v1/approval/pipeline/run", {
        document_id: docId, database: database || "Betonbau", page_map: pageMap,
      });
      if (!data.job_id) { // synchronous fallback (jobs disabled)
        if (pollTokenRef.current === token) {
          setBrief(data.result); setPhase("done"); loadSignoffs(docId);
        }
        return;
      }
      setActiveJob({ jobId: data.job_id, step: "run", docId });
      setNotice("Extraktion läuft im Hintergrund — das Ergebnis erscheint hier, sobald es fertig ist.");
      pollJob(data.job_id, token, {
        onDone: (job) => {
          clearActiveJob(); setNotice(""); setBrief(job.result); setPhase("done"); loadSignoffs(docId);
        },
        onFailed: (msg) => {
          clearActiveJob(); setNotice("");
          setError(`Extraction failed: ${msg}`); setPhase("mapped");
        },
      });
    } catch (e) {
      setNotice(""); setError(`Extraction failed: ${e.message}`); setPhase("mapped");
    }
  }, [docId, database, pageMap, startJob, pollJob, setActiveJob, clearActiveJob, loadSignoffs]);

  // Re-attach on mount to a job that was still running when we navigated away.
  const reattachedRef = useRef(false);
  useEffect(() => {
    if (reattachedRef.current || !activeJob?.jobId) return;
    reattachedRef.current = true;
    const { jobId, step, docId: savedDoc } = activeJob;
    const token = {};
    pollTokenRef.current = token;
    if (savedDoc) setDocId(savedDoc);
    if (step === "run") {
      setPhase("extracting");
      setNotice("Extraktion läuft im Hintergrund…");
      pollJob(jobId, token, {
        onDone: (job) => {
          clearActiveJob(); setNotice(""); setBrief(job.result); setPhase("done"); loadSignoffs(savedDoc);
        },
        onFailed: (msg) => {
          clearActiveJob(); setNotice("");
          setError(`Extraction failed: ${msg}`); setPhase("idle");
        },
      });
    } else {
      setPhase("mapping");
      setNotice("Struktur-Analyse läuft im Hintergrund…");
      pollJob(jobId, token, {
        onDone: (job) => { clearActiveJob(); setNotice(""); setPageMap(job.result); setPhase("mapped"); },
        onFailed: (msg) => {
          clearActiveJob(); setNotice("");
          setError(`Structure analysis failed: ${msg}`); setPhase("idle");
        },
      });
    }
  }, [activeJob, pollJob, clearActiveJob, loadSignoffs]);

  return (
    <div className="cp-root">
      <div className="ds-toolbar cp-controls">
        <DocumentPicker
          id="cp-doc"
          label="Z-document"
          placeholder="— select an approval document —"
          documents={zDocs}
          value={docId}
          onChange={(id) => { setDocId(id); setPageMap(null); setBrief(null); setPhase("idle"); }}
        />
        <button className="ds-btn ds-btn--primary" onClick={analyse} disabled={!docId || phase === "mapping" || phase === "extracting"}>
          {phase === "mapping" ? "Analysing…" : "1 · Analyse structure"}
        </button>
      </div>

      {error && <div className="ds-alert ds-alert--danger ds-flush">{error}</div>}
      {notice && <div className="ds-alert ds-flush ds-alert--info">{notice}</div>}

      {/* Checkpoint 1: editable page map */}
      {pageMap && (
        <div className="ds-card cp-card">
          <div className="ds-section-title cp-card-head">Checkpoint 1 — confirm section → page mapping
            <span className="ds-badge">{pageMap.document_type}</span></div>
          <DataTable>
            <thead><tr><th>Section</th><th>Label</th><th>Pages (comma-separated)</th></tr></thead>
            <tbody>
              {pageMap.sections.map((s, i) => (
                <tr key={s.key}>
                  <td>{SECTION_LABELS[s.key] || s.key}</td>
                  <td>{s.label}</td>
                  <td><input className="ds-input ds-input--sm cp-pages" defaultValue={(s.pages || []).join(", ")}
                        aria-label={`Pages for ${SECTION_LABELS[s.key] || s.key}`}
                        onBlur={(e) => updatePages(i, e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </DataTable>
          <div className="cp-card-actions">
            <button className="ds-btn ds-btn--primary" onClick={extract} disabled={phase === "extracting"}>
              {phase === "extracting" ? "Extracting (this can take a few minutes)…" : "2 · Confirm & extract"}
            </button>
          </div>
        </div>
      )}

      {/* Checkpoint 2: structured brief */}
      {brief && (
        <div className="ds-card cp-card">
          <div className="ds-section-title cp-card-head">Compliance brief — {stripUuid(brief.document_name)}</div>
          {(brief.gaps?.length > 0 || brief.flags?.length > 0) && (
            <div className="cp-missing">
              {brief.flags?.map((f, i) => <div key={`f${i}`} className="cp-flag">⚑ [{f.kind}] {f.detail}{f.source ? ` (${f.source.location})` : ""}</div>)}
              {brief.gaps?.map((g, i) => <div key={`g${i}`}>▢ {g}</div>)}
            </div>
          )}

          {brief.open_items?.length > 0 && (
            <div className="cp-section">
              <h4 className="cp-section-head">Open items / Flags</h4>
              <DataTable>
                <thead><tr><th>Kategorie</th><th>Detail</th><th>Quelle</th></tr></thead>
                <tbody>
                  {brief.open_items.map((it, i) => (
                    <tr key={i}><td><Badge tone="warning">{it.category}</Badge></td>
                      <td>{it.detail}</td><td><SourceCell source={it.source} /></td></tr>
                  ))}
                </tbody>
              </DataTable>
            </div>
          )}

          {brief.source_images?.length > 0 && (
            <div className="cp-section">
              <h4 className="cp-section-head">Source snippets (diagrams)</h4>
              <div className="cp-snippets">
                {brief.source_images.map((img, i) => (
                  <figure key={i} className="cp-snippet">
                    <img src={`${API_BASE_URL}${img.url}`} alt={img.label} loading="lazy" />
                    <figcaption>{img.label}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          )}
          {brief.sections.map((s, i) => (
            <div key={i} className="cp-section">
              <h4 className="cp-section-head">
                {SECTION_LABELS[s.kind] || s.kind}
                {signoffs[s.kind] ? (
                  <span className="ds-badge ds-badge--success ds-push-end">✓ {signoffs[s.kind].user} · {(signoffs[s.kind].at || "").slice(0, 10)}</span>
                ) : (
                  <button className="ds-btn ds-btn--primary ds-btn--sm ds-push-end" onClick={() => approveSection(s.kind)}>Approve section</button>
                )}
              </h4>
              <SectionView section={s} />
            </div>
          ))}

          {brief.drawing_dimensions?.length > 0 && (
            <div className="cp-section">
              <h4 className="cp-section-head">Drawing dimensions <Badge tone="warning">Verify</Badge></h4>
              <DataTable>
                <thead><tr><th>Label</th><th>Wert</th><th>Confidence</th></tr></thead>
                <tbody>
                  {brief.drawing_dimensions.map((d, i) => (
                    <tr key={i}><td>{d.label}</td><td>{d.value_mm || "—"}</td><td>{d.confidence}</td></tr>
                  ))}
                </tbody>
              </DataTable>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
